import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { VERIFIED_MEMBERS } from './VerifyCertificate';
import { Award, ShieldCheck, CheckCircle2, Building, ExternalLink, ArrowRight, FileText, Download } from 'lucide-react';


const FacultyAvatar = ({ name, refId, size = 60 }) => {
    const [imgError, setImgError] = useState(false);
    const cleanRef = refId ? refId.replace(/\//g, '_') : '';
    const photoPath = `/images/faculty/${cleanRef}.jpg?v=20261031_2`;
    
    const cleanParts = name.replace(/Dr\.|Mr\.|Mrs\.|Prof\.|Ms\.|Ph\.D|M\.Phil/gi, '').trim().split(' ').filter(Boolean);
    const initials = cleanParts.map(p => p[0]).slice(0, 2).join('').toUpperCase() || 'AN';

    if (!imgError && cleanRef) {
        return (
            <img 
                src={photoPath} 
                alt={name} 
                onError={() => setImgError(true)}
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2.5px solid #10b981',
                    boxShadow: '0 4px 12px rgba(4, 120, 87, 0.2)',
                    flexShrink: 0
                }}
            />
        );
    }

    return (
        <div style={{
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #064e3b 0%, #047857 50%, #065f46 100%)',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '800',
            fontSize: `${size * 0.36}px`,
            border: '2.5px solid #34d399',
            boxShadow: '0 4px 10px rgba(6, 78, 59, 0.2)',
            flexShrink: 0,
            letterSpacing: '1px'
        }}>
            <span>{initials}</span>
        </div>
    );
};

const Faculty = () => {
    const [selectedTab, setSelectedTab] = useState('All');

    const advisoryMembers = VERIFIED_MEMBERS.filter(m => m.council === 'Advisory Board');
    const academicMembers = VERIFIED_MEMBERS.filter(m => m.council === 'Academic Council');

    const displayedMembers = selectedTab === 'All'
        ? VERIFIED_MEMBERS
        : selectedTab === 'Advisory'
            ? advisoryMembers
            : academicMembers;

    return (
        <div style={styles.container}>
            <header style={styles.header}>
                <div style={styles.badge}>LEADERSHIP & ACADEMIC GOVERNANCE</div>
                <h1 style={styles.title}>Leadership, Advisory Board & Academic Council</h1>
                <p style={styles.subtitle}>
                    Distinguished Professors, Researchers & Scholars across Premier Indian Institutions (10-Year Tenure: 2026–2036)
                </p>
            </header>

            {/* Founder Profile Section */}
            <div style={styles.profileSection}>
                <div style={styles.imageCol}>
                    <img 
                        src="/images/logos/dr-sabitha-portrait.jpg" 
                        alt="Dr. S. Sabitha Shunmuga Priya" 
                        style={styles.portraitImg} 
                    />
                    <div style={styles.msmeTag}>
                        <img src="/images/logos/msme-logo.png" alt="MSME Govt of India" style={{ height: '30px', margin: '0 auto 6px' }} />
                        <span style={{ fontWeight: '700' }}>Ministry of MSME, Govt. of India</span>
                        <span style={{ fontSize: '11px', opacity: 0.85, fontFamily: 'monospace' }}>UDYAM-TN-32-0048110</span>
                    </div>
                </div>

                <div style={styles.bioCol}>
                    <h2 style={styles.name}>Dr. S. Sabitha Shunmuga Priya</h2>
                    <div style={styles.designationBlock}>
                        <div style={styles.rolePrimary}>Chairman & Chief Mentor, Arivu Nest Research and Development</div>
                        <div style={styles.roleSecondary}>Founder & Proprietrix, Jjegan Graphers (Virudhunagar & Kovilpatti)</div>
                        <div style={styles.roleAcademic}>Assistant Professor in English, KGISL Institute of Technology</div>
                    </div>

                    <p style={styles.bioText}>
                        Dr. S. Sabitha Shunmuga Priya is an accomplished educator, author, and academician with over <strong>11 years of university teaching experience</strong> and <strong>5 years of dedicated doctoral & postdoctoral-level communication research</strong>. 
                        She founded <strong>Arivu Nest Research and Development</strong> as a pioneering unit of <strong>Jjegan Graphers</strong> to integrate empirical research, student incubation, and AI-enabled language fluency across institutions.
                    </p>

                    <div style={styles.statsGrid}>
                        <div style={styles.statBox}>
                            <div style={styles.statNum}>11+</div>
                            <div style={styles.statLabel}>Years Teaching</div>
                        </div>
                        <div style={styles.statBox}>
                            <div style={styles.statNum}>5+</div>
                            <div style={styles.statLabel}>Years Research</div>
                        </div>
                        <div style={styles.statBox}>
                            <div style={styles.statNum}>28</div>
                            <div style={styles.statLabel}>ISBN Articles</div>
                        </div>
                        <div style={styles.statBox}>
                            <div style={styles.statNum}>16</div>
                            <div style={styles.statLabel}>ISSN Journals</div>
                        </div>
                        <div style={styles.statBox}>
                            <div style={styles.statNum}>3</div>
                            <div style={styles.statLabel}>Books Edited</div>
                        </div>
                        <div style={styles.statBox}>
                            <div style={styles.statNum}>4x</div>
                            <div style={styles.statLabel}>World Records</div>
                        </div>
                    </div>
                </div>
            </div>

                        {/* Executive Directorate Section */}
            <div style={{ marginTop: '50px' }}>
                <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '5px 14px', borderRadius: '999px', fontSize: '11px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>
                        EXECUTIVE GOVERNANCE &amp; OPERATIONS
                    </div>
                    <h2 style={{ fontSize: 'clamp(22px, 3.5vw, 30px)', color: '#0f172a', fontWeight: '800', margin: '0 0 8px' }}>
                        Executive Directorate &amp; Key Officers
                    </h2>
                    <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '650px', margin: '0 auto' }}>
                        Leading institutional operations, technological architecture, and fiscal administration.
                    </p>
                </div>

                <div style={styles.execGrid}>
                    <div style={styles.execCard}>
                        <div style={styles.execHeader}>
                            <div style={styles.execAvatar}>NM</div>
                            <div>
                                <h3 style={styles.execCardName}>Nandhakumar Murugan</h3>
                                <div style={styles.execCardRole}>Chief Operations Officer (COO) / Head of Technology &amp; Strategy</div>
                            </div>
                        </div>
                        <p style={styles.execCardDesc}>
                            Directs institutional operations, strategic planning, verified accreditation workflows, digital initiatives, and academic partnerships.
                        </p>
                    </div>

                    <div style={styles.execCard}>
                        <div style={styles.execHeader}>
                            <img src="/images/faculty/mohamed_irfan.jpg?v=20261028" alt="Mohamed Irfan" style={{ ...styles.execAvatar, objectFit: "cover", padding: 0 }} onError={(e) => { e.target.style.display = "none"; }} />
                            <div>
                                <h3 style={styles.execCardName}>Mohamed Irfan</h3>
                                <div style={styles.execCardRole}>Chief Technology Officer (CTO) / Head of Operations &amp; Administration</div>
                            </div>
                        </div>
                        <p style={styles.execCardDesc}>
                            Oversees technological infrastructure, systems engineering, platform development, and organizational administration.
                        </p>
                    </div>

                    <div style={styles.execCard}>
                        <div style={styles.execHeader}>
                            <div style={{ ...styles.execAvatar, background: 'linear-gradient(135deg, #b45309 0%, #d97706 100%)' }}>MJ</div>
                            <div>
                                <h3 style={styles.execCardName}>Mrs. Jothy</h3>
                                <div style={{ ...styles.execCardRole, color: '#b45309' }}>Treasurer / Head of Finance Management</div>
                            </div>
                        </div>
                        <p style={styles.execCardDesc}>
                            Supervises financial accounts, treasury governance, budget allocations, and statutory fiscal compliance under Jjegan Graphers.
                        </p>
                    </div>
                </div>
            </div>

            {/* Advisory Board & Academic Council Directory */}
            <div style={{ marginTop: '70px' }}>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '5px 14px', borderRadius: '999px', fontSize: '11px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px' }}>
                        <ShieldCheck size={14} /> OFFICIAL 10-YEAR TENURE (2026 – 2036)
                    </div>
                    <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', color: '#0f172a', fontWeight: '800', margin: '0 0 10px' }}>
                        Advisory Board & Academic Council Members
                    </h2>
                    <p style={{ color: '#64748b', fontSize: '14.5px', maxWidth: '700px', margin: '0 auto' }}>
                        Solely signed & accredited by Dr. S. Sabitha Shunmuga Priya, Chairman. All credentials verifiable online via Reference ID.
                    </p>

                    {/* Filter Tabs */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '22px' }}>
                        {[
                            { id: 'All', label: `All Inducted Members (${VERIFIED_MEMBERS.length})` },
                            { id: 'Advisory', label: `Advisory Board (${advisoryMembers.length})` },
                            { id: 'Academic', label: `Academic Council (${academicMembers.length})` },
                        ].map(t => (
                            <button
                                key={t.id}
                                onClick={() => setSelectedTab(t.id)}
                                style={{
                                    padding: '8px 18px',
                                    borderRadius: '8px',
                                    border: selectedTab === t.id ? '2px solid #059669' : '1px solid #cbd5e1',
                                    background: selectedTab === t.id ? '#059669' : '#ffffff',
                                    color: selectedTab === t.id ? '#ffffff' : '#334155',
                                    fontWeight: '700',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Member Grid */}
                <div style={styles.membersGrid}>
                    {displayedMembers.map(m => (
                        <div key={m.refId} style={styles.memberCard}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <span style={styles.refIdPill}>{m.refId}</span>
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: '800',
                                    padding: '3px 8px',
                                    borderRadius: '6px',
                                    background: m.council === 'Advisory Board' ? '#ecfdf5' : '#eff6ff',
                                    color: m.council === 'Advisory Board' ? '#047857' : '#1d4ed8'
                                }}>
                                    {m.council}
                                </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px', marginTop: '6px' }}>
                                <FacultyAvatar name={m.name} refId={m.refId} size={56} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <h3 style={{ ...styles.memberCardName, margin: 0, fontSize: '16px', lineHeight: 1.3 }}>{m.name}</h3>
                                    <div style={{ ...styles.memberCardDesig, marginTop: '3px', fontSize: '12.5px' }}>{m.designation}</div>
                                </div>
                            </div>
                            
                            <div style={styles.memberCardInst}>
                                <Building size={14} color="#64748b" style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span>{m.institution}</span>
                            </div>

                            <div style={styles.memberCardFooter}>
                                <span style={{ fontSize: '11.5px', color: '#059669', fontWeight: '700' }}>
                                    ✓ Verified 10-Yr Appointment
                                </span>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {m.driveUrl && (
                                        <a
                                            href={m.driveUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={styles.downloadPdfLink}
                                            title="View & Download Official PDF Certificate"
                                        >
                                            <FileText size={12} />
                                            <span>PDF</span>
                                            <ExternalLink size={10} />
                                        </a>
                                    )}
                                    <Link 
                                        to={`/verify?id=${encodeURIComponent(m.refId)}`} 
                                        style={styles.verifyLink}
                                    >
                                        Verify &rarr;
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Apply for Council Banner */}
                <div style={styles.applyBanner}>
                    <div>
                        <h3 style={{ margin: '0 0 6px', fontSize: '20px', color: '#0f172a', fontWeight: '800' }}>
                            Join the Arivu Nest Advisory Board & Academic Council
                        </h3>
                        <p style={{ margin: 0, color: '#475569', fontSize: '14px', maxWidth: '650px' }}>
                            We invite esteemed faculty, HoDs, and research scholars to contribute towards curriculum innovation, FDPs, and publication mentorship.
                        </p>
                    </div>
                    <a 
                        href="https://forms.gle/UfkUncKELYenUJE46" 
                        target="_blank" 
                        rel="noreferrer" 
                        style={styles.applyBtn}
                    >
                        Apply for Induction &rarr;
                    </a>
                </div>
            </div>

            {/* Institutional Patrons */}
            <div style={styles.patronsSection}>
                <h3 style={styles.sectionTitle}>🏛️ Institutional Guidance & Patrons</h3>
                <p style={{ color: '#475569', fontSize: '14.5px', marginBottom: '16px' }}>
                    Arivu Nest R&D flourishes under the institutional guidance and mentorship of senior leadership from <strong>Jjegan Graphers</strong> (Virudhunagar & Kovilpatti):
                </p>
                <div style={styles.patronsGrid}>
                    <div style={styles.patronCard}>
                        <img src="/images/logos/jjegan-logo.png" alt="Jjegan Graphers" style={{ height: '38px', marginBottom: '10px' }} />
                        <h4 style={{ margin: '0 0 4px', fontSize: '16px', color: '#0f172a' }}>Mr. S. Kothala Radhakrishnan</h4>
                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Co-Founder, Jjegan Graphers &bull; Virudhunagar</p>
                    </div>
                    <div style={styles.patronCard}>
                        <img src="/images/logos/jjegan-logo.png" alt="Jjegan Graphers" style={{ height: '38px', marginBottom: '10px' }} />
                        <h4 style={{ margin: '0 0 4px', fontSize: '16px', color: '#0f172a' }}>Mr. K. Jeganathan</h4>
                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Chairman / Proprietor, Jjegan Graphers &bull; Virudhunagar</p>
                    </div>
                </div>
            </div>

            {/* Contact Banner */}
            <div style={styles.contactBanner}>
                <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: '18px', color: '#0f172a' }}>Direct Academic & Research Correspondence</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '13.5px' }}>Official inquiries regarding conferences, FDPs, student internships, or research papers.</p>
                </div>
                <a href="https://mail.google.com/mail/?view=cm&fs=1&to=dr.sabitha@arivunest.com" target="_blank" rel="noopener noreferrer" style={styles.contactBtn}>Contact Dr. Sabitha &rarr;</a>
            </div>
        </div>
    );
};

const styles = {
    container: { maxWidth: '1100px', margin: '0 auto', padding: 'clamp(40px, 6vw, 80px) 20px', fontFamily: "'Inter', sans-serif" },
    header: { textAlign: 'center', marginBottom: '50px' },
    badge: { display: 'inline-block', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '5px 14px', borderRadius: '999px', fontSize: '11px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '14px' },
    title: { fontSize: 'clamp(26px, 5vw, 38px)', color: '#0f172a', fontWeight: '800', margin: '0 0 10px 0', letterSpacing: '-0.5px' },
    subtitle: { fontSize: '15px', color: '#059669', fontWeight: '600', maxWidth: '750px', margin: '0 auto' },
    profileSection: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '36px', alignItems: 'start', background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' },
    imageCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
    portraitImg: { width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #10b981', boxShadow: '0 8px 24px rgba(16, 185, 129, 0.25)' },
    msmeTag: { background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 16px', borderRadius: '10px', fontSize: '12px', color: '#166534', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '2px', width: '100%', maxWidth: '240px' },
    bioCol: { display: 'flex', flexDirection: 'column' },
    name: { fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '0 0 10px' },
    designationBlock: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' },
    rolePrimary: { fontSize: '14.5px', fontWeight: '700', color: '#059669' },
    roleSecondary: { fontSize: '13.5px', color: '#475569', fontWeight: '600' },
    roleAcademic: { fontSize: '13px', color: '#64748b' },
    bioText: { fontSize: '14px', lineHeight: '1.7', color: '#334155', margin: '0 0 20px' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' },
    statBox: { textAlign: 'center' },
    statNum: { fontSize: '20px', fontWeight: '800', color: '#059669' },
    statLabel: { fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' },
    execGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(310px, 1fr))', gap: '20px' },
    execCard: { background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '22px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' },
    execHeader: { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' },
    execAvatar: { width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, #064e3b 0%, #047857 100%)', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '16px', flexShrink: 0, boxShadow: '0 4px 10px rgba(4, 120, 87, 0.2)' },
    execCardName: { margin: 0, fontSize: '16.5px', fontWeight: '800', color: '#0f172a' },
    execCardRole: { fontSize: '12.5px', color: '#059669', fontWeight: '700', marginTop: '2px' },
    execCardDesc: { margin: 0, fontSize: '13px', color: '#64748b', lineHeight: '1.6' },
    membersGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginTop: '24px' },
    memberCard: { background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '22px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.03)', transition: 'transform 0.2s, box-shadow 0.2s' },
    refIdPill: { fontFamily: 'monospace', fontSize: '12px', fontWeight: '800', color: '#059669', background: '#f0fdf4', padding: '3px 8px', borderRadius: '4px', border: '1px solid #bbf7d0' },
    memberCardName: { margin: '0 0 4px', fontSize: '17px', fontWeight: '800', color: '#0f172a' },
    memberCardDesig: { fontSize: '13px', color: '#059669', fontWeight: '600', marginBottom: '8px' },
    memberCardInst: { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12.5px', color: '#64748b', lineHeight: '1.5', marginBottom: '16px' },
    memberCardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '12px', marginTop: 'auto' },
    verifyLink: { fontSize: '12px', fontWeight: '700', color: '#059669', textDecoration: 'none' },
    downloadPdfLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: '#ecfdf5',
        color: '#047857',
        border: '1px solid #a7f3d0',
        padding: '3px 8px',
        borderRadius: '5px',
        fontSize: '11px',
        fontWeight: '700',
        textDecoration: 'none',
    },
    applyBanner: { marginTop: '40px', background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)', border: '1.5px solid #a7f3d0', borderRadius: '16px', padding: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' },
    applyBtn: { background: '#059669', color: '#ffffff', textDecoration: 'none', padding: '12px 24px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)', whiteSpace: 'nowrap' },
    patronsSection: { marginTop: '50px' },
    sectionTitle: { fontSize: '20px', fontWeight: '800', color: '#0f172a', marginBottom: '18px', borderBottom: '2px solid #f1f5f9', paddingBottom: '8px' },
    patronsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' },
    patronCard: { background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' },
    contactBanner: { marginTop: '40px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '14px', padding: '24px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' },
    contactBtn: { background: '#0f172a', color: '#ffffff', textDecoration: 'none', padding: '12px 22px', borderRadius: '8px', fontWeight: '700', fontSize: '13.5px' }
};

export default Faculty;
